import React, { useState, useEffect, useRef } from 'react';
import { X, Mail, Clock, RefreshCw, CheckCircle, AlertCircle } from 'lucide-react';

const OTPModal = ({
  isOpen,
  onClose,
  onVerify,
  onResend,
  email,
  isLoading = false,
  error = null,
  remainingAttempts = 3,
  timerSeconds = 60,
  onTimerComplete
}) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [activeIndex, setActiveIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(timerSeconds);
  const [isResending, setIsResending] = useState(false);
  const [localError, setLocalError] = useState(null);
  const inputRefs = useRef([]);

  // Sync and reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 150);
      setTimeLeft(timerSeconds);
      setOtp(['', '', '', '', '', '']);
      setActiveIndex(0);
      setLocalError(null);
      return () => clearTimeout(timer);
    }
  }, [isOpen, timerSeconds]);

  // Countdown timer logic
  useEffect(() => {
    if (!isOpen || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onTimerComplete?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, timeLeft, onTimerComplete]);

  // Handle single character inputs
  const handleChange = (index, value) => {
    const numericValue = value.replace(/\D/g, '');
    const newOtp = [...otp];

    if (!numericValue) {
      newOtp[index] = '';
      setOtp(newOtp);
      return;
    }

    const singleDigit = numericValue.slice(-1);
    newOtp[index] = singleDigit;
    setOtp(newOtp);
    setLocalError(null);

    // Dynamic auto-advance focus
    if (index < 5) {
      setActiveIndex(index + 1);
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-fire submission event if the last box is successfully populated
    if (newOtp.every(val => val !== '')) {
      handleSubmit(newOtp.join(''));
    }
  };

  // Handle fine-grained key navigation (backspace, arrows)
  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace') {
      const newOtp = [...otp];
      if (!otp[index] && index > 0) {
        newOtp[index - 1] = '';
        setOtp(newOtp);
        setActiveIndex(index - 1);
        inputRefs.current[index - 1]?.focus();
      } else {
        newOtp[index] = '';
        setOtp(newOtp);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      setActiveIndex(index - 1);
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      setActiveIndex(index + 1);
      inputRefs.current[index + 1]?.focus();
    } else if (e.key === 'Enter') {
      const fullOtp = otp.join('');
      if (fullOtp.length === 6) {
        handleSubmit(fullOtp);
      }
    }
  };

  // Smart regex clipboard paste integration
  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').trim();
    const numbers = pasteData.replace(/\D/g, '').slice(0, 6);
    
    if (numbers.length === 6) {
      const newOtp = numbers.split('');
      setOtp(newOtp);
      setActiveIndex(5);
      handleSubmit(numbers);
    } else {
      const newOtp = [...otp];
      for (let i = 0; i < Math.min(numbers.length, 6); i++) {
        newOtp[i] = numbers[i];
      }
      setOtp(newOtp);
      const nextIndex = Math.min(numbers.length, 5);
      setActiveIndex(nextIndex);
      inputRefs.current[nextIndex]?.focus();
    }
  };

  const handleSubmit = (otpValue) => {
    const finalCode = otpValue || otp.join('');
    
    if (finalCode.length !== 6) {
      setLocalError('Please fill out the complete 6-digit confirmation code.');
      return;
    }

    setLocalError(null);
    onVerify(finalCode);
  };

  const handleResend = async () => {
    if (isResending || timeLeft > 0) return;
    
    setIsResending(true);
    try {
      await onResend();
      setTimeLeft(timerSeconds);
      setOtp(['', '', '', '', '', '']);
      setActiveIndex(0);
      setLocalError(null);
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 150);
    } catch (err) {
      setLocalError('Failed to resend confirmation code. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/50 backdrop-blur-md p-0 sm:p-4 transition-all duration-300">
      
      {/* Container Card: Responsive Bottom-sheet for mobile layout, clean standard modal for desktop */}
      <div className="relative w-full max-w-md bg-white rounded-t-[24px] sm:rounded-2xl shadow-2xl transform transition-all duration-300 max-h-[94vh] flex flex-col overflow-hidden border border-slate-100">
        
        {/* Visual Mobile Swipe Indicator Handle */}
        <div className="w-12 h-1 bg-slate-200 rounded-full mx-auto my-3.5 sm:hidden flex-shrink-0" />

        {/* Global Modal Escape Target Clicker */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 sm:top-5 sm:right-5 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-xl transition-all"
          aria-label="Close modal"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="overflow-y-auto px-6 pb-8 pt-4 sm:pt-8 flex-1">
          {/* Central Context Icon Banner header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-indigo-50 rounded-2xl mb-4 border border-indigo-100/50">
              <Mail className="h-5 w-5 text-indigo-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-900 tracking-tight sm:text-2xl">Confirm your email</h2>
            <p className="mt-2 text-sm text-slate-500 max-w-xs mx-auto">
              We've sent a 6-digit verification security key code to:
            </p>
            <div className="mt-2 px-3 py-1 bg-slate-50 text-slate-700 font-medium text-sm rounded-lg inline-block max-w-full truncate border border-slate-100">
              {email}
            </div>
          </div>

          {/* Alert Callouts */}
          {(localError || error) && (
            <div className="mb-5 p-3.5 bg-rose-50 border border-rose-100 rounded-xl flex items-start gap-2.5 animate-fadeIn">
              <AlertCircle className="h-4 w-4 text-rose-500 flex-shrink-0 mt-0.5" />
              <p className="text-xs font-semibold text-rose-800 leading-normal">{localError || error}</p>
            </div>
          )}

          {/* Secure Flex Grid Input Box Display */}
          <div className="flex justify-between gap-2 max-w-sm mx-auto mb-6">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                maxLength={1}
                value={digit}
                onFocus={() => setActiveIndex(index)}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                className={`
                  w-full h-14 text-center text-xl font-bold rounded-xl border
                  focus:outline-none transition-all duration-150 select-all font-mono
                  ${digit 
                    ? 'border-indigo-600 bg-indigo-50/20 text-indigo-900' 
                    : 'border-slate-200 bg-white text-slate-800 hover:border-slate-300'
                  }
                  ${activeIndex === index
                    ? 'border-indigo-600 ring-4 ring-indigo-50/60 shadow-inner' 
                    : ''
                  }
                  ${(localError || error) 
                    ? 'border-rose-300 bg-rose-50/20 focus:border-rose-500 focus:ring-rose-50' 
                    : ''
                  }
                `}
                placeholder="•"
                disabled={isLoading}
              />
            ))}
          </div>

          {/* Remaining Attempts Banner */}
          {remainingAttempts < 3 && (
            <div className="text-center mb-6">
              <span className="inline-flex items-center text-xs font-semibold text-amber-800 bg-amber-50 rounded-full py-1 px-3 border border-amber-100">
                ⚠️ Only {remainingAttempts} standard verification attempts remaining
              </span>
            </div>
          )}

          {/* Functional Parameter Metadata Row */}
          <div className="flex items-center justify-between gap-4 text-xs sm:text-sm border-t border-b border-slate-100 py-3.5 mb-6">
            <div className="flex items-center gap-1.5 text-slate-500">
              <Clock className="h-4 w-4 text-slate-400" />
              <span>Expires in:</span>
              <span className={`font-mono font-bold ${timeLeft < 20 ? 'text-rose-600' : 'text-slate-800'}`}>
                {formatTime(timeLeft)}
              </span>
            </div>
            
            <button
              onClick={handleResend}
              disabled={isResending || timeLeft > 0}
              className={`
                flex items-center gap-1.5 font-semibold text-xs transition-all duration-150
                ${timeLeft > 0 || isResending
                  ? 'text-slate-400 cursor-not-allowed'
                  : 'text-indigo-600 hover:text-indigo-700 active:scale-95'
                }
              `}
            >
              <RefreshCw className={`h-3 w-3 ${isResending ? 'animate-spin' : ''}`} />
              {isResending ? 'Resending...' : timeLeft > 0 ? 'Resend code later' : 'Resend code'}
            </button>
          </div>

          {/* Unified Form Submission Button */}
          <button
            onClick={() => handleSubmit()}
            disabled={isLoading || otp.join('').length !== 6}
            className={`
              w-full py-3.5 px-4 rounded-xl font-semibold text-white text-sm
              transition-all duration-150 flex items-center justify-center gap-2 shadow-md
              ${isLoading || otp.join('').length !== 6
                ? 'bg-slate-200 text-slate-400 shadow-none cursor-not-allowed'
                : 'bg-indigo-600 hover:bg-indigo-700 active:scale-[0.99] shadow-indigo-100'
              }
            `}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Validating credentials...</span>
              </>
            ) : (
              <>
                <CheckCircle className="h-4 w-4" />
                <span>Verify code</span>
              </>
            )}
          </button>

          {/* Secondary Informational Support Target Container */}
          <p className="mt-5 text-center text-xs text-slate-400">
            No notification received? Check your filters or{' '}
            <button
              onClick={handleResend}
              disabled={isResending || timeLeft > 0}
              className="text-indigo-600 font-semibold hover:underline disabled:text-slate-400 disabled:no-underline"
            >
              request another
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default OTPModal;